import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { ArrowRight, Search, Loader2 } from 'lucide-react';

const heroImages = [
  'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=600&h=400&q=72&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=400&q=72&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&q=72&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=600&h=400&q=72&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&h=400&q=72&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600&h=400&q=72&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=600&h=400&q=72&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1546961342-ea5f62d5a27b?w=600&h=400&q=72&auto=format&fit=crop',
];

const chaosItems = [
  { icon: '📱', text: '100+ WhatsApp messages per project' },
  { icon: '📄', text: 'PDFs shared in random groups' },
  { icon: '⏳', text: '3–6 weeks to confirm talent' },
  { icon: '🙈', text: 'No availability info, endless follow-ups' },
  { icon: '💸', text: 'Budget wasted on uncertainty' },
];

const castdeckItems = [
  { icon: '🎯', text: 'One platform, every brief organised' },
  { icon: '🖼', text: 'HD profiles, showreels, real availability' },
  { icon: '⚡', text: 'Confirm talent in under 10 days' },
  { icon: '🤖', text: 'AI search — describe the look, find the person' },
  { icon: '🔒', text: 'NDA-protected, professional, accountable' },
];

const talentStrip = [
  { name: 'Rahul Kumar', role: 'Actor · Mumbai', img: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=220&h=320&q=72&auto=format&fit=crop', avail: true },
  { name: 'Priya Sharma', role: 'Model · Delhi', img: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=220&h=320&q=72&auto=format&fit=crop', avail: false },
  { name: 'Arjun Mehta', role: 'Actor · Bangalore', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=220&h=320&q=72&auto=format&fit=crop', avail: true },
  { name: 'Sneha Nair', role: 'Performer · Chennai', img: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=220&h=320&q=72&auto=format&fit=crop', avail: false },
  { name: 'Vikram Rao', role: 'Actor · Mumbai', img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=220&h=320&q=72&auto=format&fit=crop', avail: true },
  { name: 'Ananya Das', role: 'Model · Kolkata', img: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=220&h=320&q=72&auto=format&fit=crop', avail: false },
  { name: 'Karan Patel', role: 'Actor · Mumbai', img: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=220&h=320&q=72&auto=format&fit=crop', avail: true },
];

const features = [
  { num: '01', icon: '🎭', name: 'Curated Talent Pool', desc: 'Every profile is reviewed before going live. HD headshots, showreels, verified availability. No spam, no bots.', pill: 'Browse Now' },
  { num: '02', icon: '🤖', name: 'AI-Powered Search', desc: 'Describe what you need in plain language — "30-year-old male, sporty look, Hindi-English fluent, Delhi." AI surfaces the right people instantly.', pill: 'New Feature' },
  { num: '03', icon: '🎬', name: 'Project Casting Deck', desc: 'Build visual casting decks. Move talent through stages — longlist, shortlist, hold, confirmed. Share with directors and clients.', pill: 'Try Dashboard' },
  { num: '04', icon: '📋', name: 'Holds & Bookings', desc: 'Issue holds, track confirmations, manage scheduling. The entire booking workflow in one place — no more reply-all emails.', pill: 'Coming Soon' },
  { num: '05', icon: '🌏', name: 'India & International', desc: "Mumbai, Delhi, Bangalore, Chennai and beyond. Indian talent. International talent. All on one platform built for Indian productions.", pill: 'Explore' },
  { num: '06', icon: '🔒', name: 'NDA-Protected Access', desc: 'Client casting stays private. Talent profiles shared only with verified production houses. Professional grade security for every brief.', pill: 'Learn More' },
];

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div style={{ background: 'var(--ink)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      {/* NAV */}
      <nav className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="landing-nav">
        <div className="flex items-center justify-between px-6 h-14 max-w-[1400px] mx-auto">
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <div className="w-7 h-7 bg-[var(--gold)] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)' }}>
              <span style={{ fontFamily: 'var(--display)', fontSize: 14, color: '#000', letterSpacing: 2 }}>C</span>
            </div>
            <span className="font-display text-xl" style={{ color: 'var(--cream)' }}>CASTDECK</span>
            <span className="text-[9px] font-bold tracking-widest ml-1 gradient-gold" style={{ fontFamily: 'var(--head)' }}>AI-Powered</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="nav-tab active" data-testid="nav-home">Home</Link>
            <Link to={user ? '/talents' : '/login'} className="nav-tab" data-testid="nav-talent">Talent</Link>
            <Link to={user ? '/dashboard' : '/login'} className="nav-tab" data-testid="nav-dashboard">Dashboard</Link>
            <Link to="/join-as-talent" className="nav-tab" data-testid="nav-join">Join as Talent</Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <button onClick={() => navigate('/dashboard')} className="btn-gold px-5 py-2 text-xs" data-testid="dashboard-btn">
                Dashboard <span className="ml-1">→</span>
              </button>
            ) : (
              <>
                <Link to="/register" className="btn-ghost px-4 py-2 text-xs" data-testid="join-free-btn">Join Free</Link>
                <Link to="/login" className="btn-gold px-5 py-2 text-xs" data-testid="login-btn">Login →</Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden text-[var(--cream)]" onClick={() => navigate(user ? '/dashboard' : '/login')} data-testid="mobile-menu-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
          </button>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden" style={{ minHeight: 'calc(100vh - 56px)', marginTop: 56 }} data-testid="hero-section">
        {/* Mosaic background */}
        <div className="hero-mosaic">
          {heroImages.map((img, i) => (
            <div key={i} className={`hm-cell ${i === 0 ? 'large' : ''}`}>
              <img src={img} alt="" loading="lazy" />
            </div>
          ))}
        </div>
        <div className="hero-overlay" />

        {/* Hero body */}
        <div className="relative z-10 grid items-center px-6 py-16 max-w-[1400px] mx-auto" style={{ gridTemplateColumns: '1fr 420px', minHeight: 'calc(100vh - 56px)', gap: 40 }}>
          {/* Left side */}
          <div>
            {/* Ticker */}
            <div className="flex items-center gap-3 mb-8">
              <div className="px-3 py-1.5 text-[10px] font-bold tracking-widest uppercase" 
                   style={{ background: 'var(--gold-glow)', color: 'var(--gold)', border: '1px solid var(--gold-line)', fontFamily: 'var(--head)' }}
                   data-testid="live-ticker">
                Live · 3,247 Verified Profiles
              </div>
              <div className="flex-1 h-px" style={{ background: 'var(--gold-line)' }} />
            </div>

            <h1 className="font-display mb-5" style={{ fontSize: 'clamp(72px, 10vw, 130px)', lineHeight: 0.88, letterSpacing: 3, color: 'var(--cream)' }} data-testid="hero-heading">
              DITCH<br/>THE<br/>
              <span className="outline-gold" style={{ fontSize: 'clamp(72px, 10vw, 130px)' }}>CHAOS</span>
            </h1>

            <p className="mb-8 max-w-lg" style={{ fontFamily: 'var(--body)', fontSize: 15, lineHeight: 1.7, color: 'var(--warm)' }}>
              India's casting still runs on WhatsApp chaos, scattered PDFs and random referrals. CastDeck ends all of it — one AI-powered platform to find, shortlist and book talent in days, not weeks.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <button onClick={() => navigate(user ? '/talents' : '/register')} className="btn-gold px-7 py-3" data-testid="explore-talent-btn">
                Explore Talent →
              </button>
              <button onClick={() => navigate('/join-as-talent')} className="btn-ghost px-7 py-3" data-testid="join-talent-btn">
                Join as Talent
              </button>
              <span className="text-xs ml-2" style={{ color: 'var(--muted-c)' }}>Free to join · No login to apply</span>
            </div>
          </div>

          {/* Stats Card */}
          <div className="hero-stats-card hidden lg:block" data-testid="stats-card">
            <div className="text-[10px] font-bold tracking-widest uppercase mb-5" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Live Platform Stats</div>
            {[
              { color: 'var(--green)', label: 'Verified Talents', val: '3,247' },
              { color: 'var(--blue)', label: 'Productions', val: '500+' },
              { color: 'var(--gold)', label: 'Avg. Turnaround', val: '10 days' },
              { color: 'var(--purple)', label: 'Satisfaction', val: '98%' },
            ].map((row, i) => (
              <div key={i} className="flex items-center justify-between py-3" style={{ borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                <span className="flex items-center gap-2 text-xs" style={{ color: 'var(--warm)', fontFamily: 'var(--head)' }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: row.color }} />
                  {row.label}
                </span>
                <span className="text-sm font-bold" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>{row.val}</span>
              </div>
            ))}
            <div className="mt-5 flex items-center gap-3 p-3 cursor-pointer hover:opacity-80 transition-opacity" style={{ background: 'var(--faint)', border: '1px solid rgba(255,255,255,.06)' }}>
              <span className="text-xl">📱</span>
              <div className="flex-1">
                <div className="text-xs font-bold" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>Replace your casting group</div>
                <div className="text-[10px]" style={{ color: 'var(--muted-c)' }}>Stop the WhatsApp chaos today</div>
              </div>
              <span style={{ color: 'var(--gold)' }}>→</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CHAOS vs CASTDECK ═══ */}
      <section className="px-6 py-16 max-w-[1200px] mx-auto" data-testid="chaos-section">
        <div className="chaos-grid">
          {/* Bad side */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--red)' }} />
              <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: 'var(--red)', fontFamily: 'var(--head)' }}>How casting works today</span>
            </div>
            <div className="space-y-2">
              {chaosItems.map((item, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3" style={{ background: 'rgba(224,86,86,.04)', border: '1px solid rgba(224,86,86,.1)' }}>
                  <span className="text-base">{item.icon}</span>
                  <span className="text-sm" style={{ color: 'var(--warm)', fontFamily: 'var(--body)' }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* VS */}
          <div className="flex items-center justify-center">
            <div className="font-display text-3xl" style={{ color: 'var(--muted-c)', letterSpacing: 3, writingMode: 'vertical-rl' }}>VS</div>
          </div>

          {/* Good side */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--green)' }} />
              <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: 'var(--green)', fontFamily: 'var(--head)' }}>How CastDeck works</span>
            </div>
            <div className="space-y-2">
              {castdeckItems.map((item, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3" style={{ background: 'rgba(46,204,138,.04)', border: '1px solid rgba(46,204,138,.1)' }}>
                  <span className="text-base">{item.icon}</span>
                  <span className="text-sm" style={{ color: 'var(--warm)', fontFamily: 'var(--body)' }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TALENT MOSAIC STRIP ═══ */}
      <section className="py-16" data-testid="talent-strip">
        <div className="px-6 max-w-[1400px] mx-auto mb-10">
          <div className="flex items-end justify-between">
            <div>
              <div className="font-display text-5xl md:text-6xl" style={{ lineHeight: 1, letterSpacing: 3, color: 'var(--cream)' }}>
                FIND YOUR<br/><span className="gradient-gold">NEXT CAST</span>
              </div>
            </div>
            <div className="hidden md:block max-w-sm text-right">
              <p className="text-sm" style={{ color: 'var(--warm)', lineHeight: 1.6 }}>
                3,000+ verified actors, models and performers across India. Every profile reviewed before it goes live.
              </p>
            </div>
          </div>
        </div>
        <div className="talent-mosaic-strip">
          {talentStrip.map((t, i) => (
            <div key={i} className="tm-card" data-testid={`talent-strip-${i}`}>
              <img src={t.img} alt={t.name} loading="lazy" />
              <div className="absolute top-2 left-2 z-10 px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase"
                   style={{ background: t.avail ? 'var(--green)' : 'var(--red)', color: '#fff' }}>
                {t.avail ? 'Available' : 'Busy'}
              </div>
              <div className="absolute bottom-3 left-3 right-3 z-10">
                <div className="text-sm font-bold" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>{t.name}</div>
                <div className="text-[11px]" style={{ color: 'var(--warm)' }}>{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section className="px-6 py-20 max-w-[1400px] mx-auto" data-testid="features-section">
        <div className="mb-3 text-[10px] font-bold tracking-widest uppercase" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Why CastDeck</div>
        <h2 className="mb-12" style={{ fontFamily: 'var(--head)', fontSize: 42, fontWeight: 900, letterSpacing: -1, lineHeight: 1.05, color: 'var(--cream)' }}>
          Casting made<br/><em style={{ fontFamily: 'var(--serif)', fontWeight: 400 }}>intelligent.</em>
        </h2>
        <div className="feat-grid-v3">
          {features.map((f, i) => (
            <div key={i} className="p-6 transition-all hover:-translate-y-1 group" 
                 style={{ background: 'var(--ink2)', border: '1px solid rgba(255,255,255,.06)' }}
                 data-testid={`feature-card-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="font-display text-2xl" style={{ color: 'rgba(255,255,255,.08)', letterSpacing: 2 }}>{f.num}</span>
                <span className="text-2xl">{f.icon}</span>
              </div>
              <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'var(--head)', color: 'var(--cream)' }}>{f.name}</h3>
              <p className="text-xs mb-4" style={{ color: 'var(--warm)', lineHeight: 1.6 }}>{f.desc}</p>
              <span className="inline-block px-3 py-1 text-[9px] font-bold tracking-widest uppercase"
                    style={{ background: 'var(--gold-glow)', color: 'var(--gold)', border: '1px solid var(--gold-line)', fontFamily: 'var(--head)' }}>
                {f.pill}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ NUMBERS BAND ═══ */}
      <section className="numbers-band-v3" data-testid="numbers-band">
        {[
          { num: '3K+', label: 'Verified Talents' },
          { num: '500+', label: 'Productions' },
          { num: '10 days', label: 'Average Turnaround' },
          { num: '98%', label: 'Satisfaction Rate' },
        ].map((n, i) => (
          <div key={i} className="text-center py-12 px-4" style={{ background: i % 2 === 0 ? 'var(--ink2)' : 'var(--ink3)', borderTop: '1px solid rgba(255,255,255,.06)' }}>
            <div className="font-display text-4xl md:text-5xl mb-1 gradient-gold" style={{ letterSpacing: 2 }}>{n.num}</div>
            <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>{n.label}</div>
          </div>
        ))}
      </section>

      {/* ═══ FINALE CTA ═══ */}
      <section className="relative py-24 px-6 overflow-hidden" data-testid="finale-cta">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, var(--ink) 0%, var(--ink3) 100%)' }} />
        <div className="absolute inset-0 opacity-5"
             style={{ backgroundImage: "url('https://images.unsplash.com/photo-1574867022210-bd9ecc413bf3?w=1200&q=30')", backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <div className="text-[10px] font-bold tracking-widest uppercase mb-6" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Built by Bread Jam Productions</div>
          <h2 className="font-display mb-6" style={{ fontSize: 'clamp(40px, 6vw, 72px)', lineHeight: 1.05, letterSpacing: 3, color: 'var(--cream)' }}>
            READY TO<br/><em className="font-serif" style={{ WebkitTextStroke: 'none' }}>CAST SMARTER?</em>
          </h2>
          <p className="mb-10 text-sm max-w-lg mx-auto" style={{ color: 'var(--warm)', lineHeight: 1.7 }}>
            Join hundreds of production houses already using CastDeck to find talent faster.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button onClick={() => navigate(user ? '/talents' : '/register')} className="btn-gold px-8 py-3" data-testid="cta-explore-btn">
              Explore Talent →
            </button>
            <button onClick={() => navigate('/join-as-talent')} className="btn-ghost px-8 py-3" data-testid="cta-join-btn">
              Join as Talent — Free
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6" style={{ background: 'var(--ink)', borderTop: '1px solid rgba(255,255,255,.06)' }}>
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[var(--gold)] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)' }}>
              <span style={{ fontFamily: 'var(--display)', fontSize: 10, color: '#000' }}>C</span>
            </div>
            <span className="font-display text-base" style={{ color: 'var(--cream)' }}>CASTDECK</span>
          </div>
          <div className="flex gap-6 text-xs" style={{ color: 'var(--muted-c)' }}>
            <a href="#" className="hover:text-[var(--cream)] transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-[var(--cream)] transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-[var(--cream)] transition-colors">Contact</a>
          </div>
          <div className="text-xs" style={{ color: 'var(--muted-c)' }}>© 2026 Bread Jam Studios. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
